// Lightweight, privacy-safe session error/network buffer + device-context
// collector for bug reports. There is intentionally NO frontend error-
// monitoring SDK; this is the minimal hand-rolled alternative attached to bug
// submissions from the Help modal.
//
// Privacy contract (the server independently re-enforces all of this in
// FeedbackContext, but we never want to even hold sensitive data client-side):
//   - Failed network entries are METADATA ONLY — URL (query string stripped),
//     method, status, timing. Never request/response bodies or headers.
//   - console.error keeps only the first STRING argument (truncated). Later
//     object arguments — which routinely carry employee/HR data — are dropped.
//   - All strings are length-capped; the buffer is a fixed-size ring.
//
// Install once, as early as possible, from the Inertia entrypoint. The install
// is idempotent (HMR / React StrictMode re-execution won't double-wrap).

const MAX_ENTRIES = 30
const MAX_STRING = 500
const INSTALL_FLAG = '__staghrBugBufferInstalled'

const buffer = []

function truncate(value) {
  if (typeof value !== 'string') return undefined
  return value.length > MAX_STRING ? value.slice(0, MAX_STRING) : value
}

// Drop query string + fragment so PII passed as query params (?ssn=, ?token=)
// never enters the buffer.
function stripQuery(url) {
  if (typeof url !== 'string') return undefined
  const cut = url.search(/[?#]/)
  return truncate(cut === -1 ? url : url.slice(0, cut))
}

function now() {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
}

function push(entry) {
  entry.timestamp = entry.timestamp || new Date().toISOString()
  buffer.push(entry)
  while (buffer.length > MAX_ENTRIES) buffer.shift()
}

function recordNetworkFailure(type, { url, method, status, start }) {
  const cleanUrl = stripQuery(url)
  // Don't let a failed feedback submission record itself into the buffer that
  // the next bug report would attach (self-contamination).
  if (cleanUrl && cleanUrl.endsWith('/feedback')) return
  push({
    type,
    url: cleanUrl,
    method: truncate(typeof method === 'string' ? method.toUpperCase() : 'GET'),
    status,
    duration_ms: Math.round(now() - start),
  })
}

// Snapshot of the recent error buffer (newest last). Returns a copy.
export function recentClientErrors() {
  return buffer.slice()
}

function deviceType(ua) {
  if (typeof ua !== 'string') return undefined
  if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile'
  if (/iPad|Tablet/i.test(ua)) return 'tablet'
  return 'desktop'
}

function readAppVersionFromDom() {
  try {
    const el = document.getElementById('app')
    if (!el || !el.dataset || !el.dataset.page) return undefined
    return JSON.parse(el.dataset.page)?.props?.app_version || undefined
  } catch {
    return undefined
  }
}

// Device/browser context collected at submit time (not buffered). app_version
// is the Inertia shared prop — callers (the Help modal) pass it from
// usePage().props; falls back to reading the initial #app data-page payload.
export function collectDeviceContext(appVersion = readAppVersionFromDom()) {
  const nav = typeof navigator !== 'undefined' ? navigator : {}
  const win = typeof window !== 'undefined' ? window : {}
  const screen = win.screen || {}
  const ctx = {
    browser: truncate(nav.userAgent),
    os: truncate(nav.platform),
    device_type: deviceType(nav.userAgent),
    viewport: win.innerWidth ? `${win.innerWidth}x${win.innerHeight}` : undefined,
    screen: screen.width ? `${screen.width}x${screen.height}` : undefined,
    url_path: truncate(win.location ? win.location.pathname : undefined),
    locale: truncate(nav.language),
    timestamp: new Date().toISOString(),
    app_version: truncate(appVersion),
  }
  // Drop undefined keys so the payload stays tight.
  Object.keys(ctx).forEach((k) => ctx[k] === undefined && delete ctx[k])
  return ctx
}

export function installBugBuffer() {
  if (typeof window === 'undefined') return
  if (window[INSTALL_FLAG]) return
  window[INSTALL_FLAG] = true

  window.addEventListener('error', (event) => {
    const message = event.message || (event.error && event.error.message) || 'Uncaught error'
    push({ type: 'uncaught_error', message: truncate(String(message)) })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message = reason && reason.message ? reason.message : String(reason)
    push({ type: 'unhandled_rejection', message: truncate(message) })
  })

  // Keep only the first string arg — later object args may carry HR/PII.
  const originalConsoleError = console.error
  console.error = function (...args) {
    const firstString = args.find((a) => typeof a === 'string')
    push({ type: 'console_error', message: truncate(firstString || '(non-string console.error)') })
    // console.error is a host method that requires `console` as its receiver.
    return originalConsoleError.apply(console, args)
  }

  if (typeof window.fetch === 'function') {
    // Bind to window: fetch throws "Illegal invocation" if called with any
    // other receiver, which a detached wrapper (this === undefined in strict
    // module scope) would otherwise trigger.
    const originalFetch = window.fetch.bind(window)
    window.fetch = function (...args) {
      const start = now()
      const input = args[0]
      const url = typeof input === 'string' ? input : (input && input.url)
      const method = (args[1] && args[1].method) || (input && input.method) || 'GET'
      return originalFetch(...args).then(
        (res) => {
          if (!res.ok) recordNetworkFailure('fetch_error', { url, method, status: res.status, start })
          return res
        },
        (err) => {
          recordNetworkFailure('fetch_error', { url, method, status: 0, start })
          throw err
        },
      )
    }
  }

  // Inertia/axios use XHR, so a fetch wrapper alone misses most app traffic.
  if (typeof XMLHttpRequest === 'function') {
    const originalOpen = XMLHttpRequest.prototype.open
    const originalSend = XMLHttpRequest.prototype.send
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__bugMethod = method
      this.__bugUrl = url
      return originalOpen.call(this, method, url, ...rest)
    }
    XMLHttpRequest.prototype.send = function (...args) {
      const start = now()
      // once: true so a reused XHR (re-open + re-send) doesn't accumulate
      // listeners and record duplicate entries.
      this.addEventListener('loadend', () => {
        const status = this.status
        if (status === 0 || status >= 400) {
          recordNetworkFailure('xhr_error', { url: this.__bugUrl, method: this.__bugMethod, status, start })
        }
      }, { once: true })
      return originalSend.apply(this, args)
    }
  }
}
